import React from 'react';
import { motion } from 'framer-motion';
import { HERO_TABS } from '../constants';

const Hero = ({ activeTab, setActiveTab, scrollToContact }) => {
    const tabs = HERO_TABS;

    const handleTabClick = (id) => {
        setActiveTab(id);
    };

    return (
        <section className="relative min-h-[850px] sm:min-h-[1000px] h-screen lg:h-screen w-full flex flex-col items-center pt-32 pb-12 bg-white overflow-visible px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl w-full mx-auto relative h-full flex flex-col items-center justify-center">
                {/* Contained Background Image Container */}
                <div className="absolute inset-0 z-0 overflow-hidden rounded-[20px]">
                    <img
                        src="/hero-bg.png"
                        alt="Excalibur Projects Luxury Residential Development Mumbai"
                        className="w-full h-full object-cover object-center"
                        fetchpriority="high"
                    />
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
                </div>

                {/* Centered Content */}
                <div className="relative z-10 w-full px-4 text-center flex flex-col items-center pb-32 sm:pb-48 lg:pb-0">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2.5 bg-black px-4 sm:px-5 py-2 rounded-full mb-8 sm:mb-10 border border-white/20 shadow-2xl"
                    >
                        <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 bg-brandGreen rounded-full shadow-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <span className="text-white text-[9px] sm:text-[11px] font-black uppercase tracking-[0.15em]">
                            79A GRs & DCPR 2034 Compliant
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="text-4xl sm:text-7xl lg:text-8xl font-bold text-white leading-[1.1] sm:leading-[1.05] tracking-tight mb-6 sm:mb-8 max-w-5xl"
                    >
                        From Vision to Victory
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="text-base sm:text-2xl text-white/80 max-w-2xl leading-relaxed font-medium drop-shadow-md"
                    >
                        Simplify your redevelopment journey with Mumbai's most trusted partner. Fast, secure, and expert-led solutions for every society.
                    </motion.p>
                </div>

                {/* Floating Search/Selector Card Straddling Section Boundary */}
                <div className="absolute bottom-0 translate-y-1/2 z-20 px-4 w-full">
                    <div className="max-w-5xl mx-auto">
                        {/* Tabs - Grid on Mobile, Flex on Desktop */}
                        <div className="grid grid-cols-2 sm:flex gap-1 sm:gap-2 mb-0 px-2 sm:px-0 sm:pl-6">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabClick(tab.id)}
                                    className={`
                                        w-full sm:w-auto px-4 sm:px-8 py-2.5 sm:py-3 rounded-t-xl sm:rounded-t-2xl font-bold text-xs sm:text-sm transition-all border-none
                                        ${(tab.id === 'Society' || tab.id === 'LandOwner') ? 'mt-12 sm:mt-0' : ''}
                                        ${tab.id === 'Investor' ? 'col-span-2 sm:col-span-1' : ''}
                                        ${activeTab === tab.id
                                            ? 'bg-white text-primary shadow-lg'
                                            : 'bg-black/30 text-white hover:bg-black/50 backdrop-blur-sm'}
                                    `}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* White Body Card */}
                        <motion.div
                            className="bg-white rounded-t-none rounded-b-[1.5rem] sm:rounded-[2.5rem] sm:rounded-tl-none p-6 sm:p-10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] border border-gray-100 flex flex-col md:flex-row gap-6 sm:gap-8 items-center justify-between"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                                <div className="flex flex-col gap-1.5 md:border-r border-gray-100 pr-4">
                                    <label className="text-tertiary text-[10px] uppercase font-black tracking-[0.1em]">User Type</label>
                                    <span className="text-lg font-medium text-primary leading-tight">{activeTab}</span>
                                    <span className="text-xs text-primary/60 font-medium">Looking for solution</span>
                                </div>

                                <div className="flex flex-col gap-1.5 md:border-r border-gray-100 pr-4">
                                    <label className="text-tertiary text-[10px] uppercase font-black tracking-[0.1em]">Target Region</label>
                                    <span className="text-lg font-medium text-primary leading-tight">Mumbai</span>
                                    <span className="text-xs text-primary/60 font-medium">All Prime Areas</span>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-tertiary text-[10px] uppercase font-black tracking-[0.1em]">Service Type</label>
                                    <span className="text-lg font-medium text-primary leading-tight">Consultation</span>
                                    <span className="text-xs text-primary/60 font-medium">Expert Help</span>
                                </div>
                            </div>

                            <button
                                onClick={scrollToContact}
                                className="w-full md:w-auto px-12 h-16 flex items-center justify-center bg-logo-gradient hover-logo-gradient text-white font-bold rounded-2xl transition-all shadow-xl transform hover:-translate-y-1 active:scale-95 whitespace-nowrap"
                            >
                                Quick Connect
                            </button>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
